import { appendFiles } from "../../../../../../../helpers/uploads";
import { deliveryLines, productName } from "./routeSheet";

// The delivery list saves exactly what the warehouse's fulfilment status
// pop-up saves (ConformModal, opened from the order view): the same call,
// the same fields in the same order, and the same pallet choice. "Can't
// deliver" adds a comment the way the order view's activity panel does.

export const FULFILLED_STATUS = 4;
export const ATTENTION_STATUS = 5;
export const FULFILLED_NAME = "FULFILLED";

const CASH_METHODS = ["Cash On Delivery", "Cash On Pickup"];
export const isCashOrder = (order) =>
  CASH_METHODS.includes(order?.payment_method_title);

// The status name the pop-up sends: the app's status list, in capitals.
export const statusName = (statuses, statusId) =>
  statuses.find((status) => status.id === statusId)?.name?.toUpperCase();

// The lines as the pop-up starts them: products from Bolt only (not lines
// synced from a marketplace), each pre-set to the smallest of the product's
// pallets that holds the line's quantity. Kept exactly as the pop-up does it,
// including that pallet quantities arrive as text and the smallest is found
// by comparing that text.
export const startLines = (order) =>
  (order?.order_details ?? [])
    .filter((item) => !item.plugin_item_id)
    .map((item) => {
      const pallets = item?.products?.product_pallets;
      if (!Array.isArray(pallets) || pallets.length === 0) return item;
      const valid = pallets.filter(
        (pallet) => pallet.quantity >= Number(item?.item_quantity ?? 0),
      );
      if (valid.length === 0) return item;
      const lowest = valid.reduce((min, curr) =>
        curr.quantity < min.quantity ? curr : min,
      );
      return {
        ...item,
        pallet_data: [
          {
            pallet_id: Number(lowest.pallet_id),
            quantity: Number(item?.item_quantity ?? 0),
          },
        ],
      };
    });

// Change pallets, as the pop-up does: ticking a pallet adds it with 0,
// unticking removes it, and the quantity is what's typed.
export const togglePallet = (line, palletId) => {
  const current = line?.pallet_data ?? [];
  const exists = current.some(
    (data) => String(data?.pallet_id) === String(palletId),
  );
  return {
    ...line,
    pallet_data: exists
      ? current.filter((data) => String(data?.pallet_id) !== String(palletId))
      : [...current, { pallet_id: parseInt(palletId, 10), quantity: 0 }],
  };
};
export const setPalletQuantity = (line, index, quantity) => ({
  ...line,
  pallet_data: (line?.pallet_data ?? []).map((data, at) =>
    at === index ? { ...data, quantity } : data,
  ),
});

// Partly delivered (Attention) saves the pop-up's per-line fields: saleable
// (the order view's "Fulfilled" Yes/No) and the saleable quantity ("Fulfilled
// Qty"). Here one count per line sets both: all of it is saleable, any less
// is that many, so the two can't disagree (the pop-up let a line be "No"
// with its whole quantity).
export const orderedCount = (line) => Number(line?.item_quantity ?? 0) || 0;
export const deliveredCount = (line) => {
  const ordered = orderedCount(line);
  if (line?.is_saleable === true || String(line?.is_saleable) === "1") {
    return ordered;
  }
  const count = Number(line?.saleable_item_quantity ?? 0) || 0;
  return Math.max(0, Math.min(ordered, count));
};
export const setDelivered = (line, count) => {
  const ordered = orderedCount(line);
  const delivered = Math.max(
    0,
    Math.min(ordered, Math.round(Number(count) || 0)),
  );
  return {
    ...line,
    is_saleable: ordered > 0 && delivered === ordered,
    saleable_item_quantity: delivered,
  };
};
// The note on a line is its fulfilment note (why it wasn't all delivered).
// The product's own note, the shipper's from the order (`note`), is never
// sent, so delivering never changes it.
export const setLineNote = (line, note) => ({
  ...line,
  fulfillment_note: note,
});

// The pop-up's save, field for field. `attachments` are the picked files as
// usePickedFiles().ready() gives them ({ file, filename }; photos shrunk).
export const buildStatusForm = ({
  order,
  statusId,
  name,
  lines,
  note,
  attachments = [],
  amountReceived,
}) => {
  const formData = new FormData();
  appendFiles(formData, attachments);
  if (statusId === ATTENTION_STATUS) {
    formData.append(
      `order_details`,
      JSON.stringify(
        lines.map(
          ({ is_saleable, saleable_item_quantity, id, fulfillment_note }) => ({
            is_saleable,
            saleable_item_quantity,
            fulfillment_note,
            id,
          }),
        ),
      ),
    );
  }
  if (name === FULFILLED_NAME) {
    formData.append(
      `order_details`,
      JSON.stringify(lines.map(({ pallet_data, id }) => ({ pallet_data, id }))),
    );
  }
  if (amountReceived) {
    formData.append(`amount_received_by_warehouse`, amountReceived ?? "");
  }
  formData.append(`note`, note ?? "");
  formData.append(`order_id`, order?.id);
  formData.append(`status`, order?.status);
  formData.append(`fulfillment_order_status`, name);
  return formData;
};

// "Can't deliver": a comment with the reason, as the order view adds one.
export const CANT_DELIVER_REASONS = [
  "Not home",
  "Refused",
  "Wrong address",
  "Other",
];

// The note saved with every delivery-list action: what happened, on which
// run (its route, date, stop and start location), what was delivered or is
// still to deliver, the cash received, and whatever was typed. The author and
// time are recorded with the note.
const itemName = (item) => productName(item) || "item";
const runDate = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
// A run's date ("2026-10-28") as that day here, not midnight UTC.
const localDay = (value) => {
  const [year, month, day] = String(value ?? "")
    .slice(0, 10)
    .split("-")
    .map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};
export const deliveryNote = ({
  kind,
  reason,
  run = {},
  stop,
  stopCount,
  order,
  lines = [],
  amount,
  typed,
  date = new Date(),
}) => {
  const what =
    kind === "delivered"
      ? "Delivered"
      : kind === "partly"
        ? "Partly delivered"
        : `Not delivered (${reason})`;
  const heading = [
    what,
    run.routeName ? `${run.routeName} run` : "Delivery run",
    // A run's own date; the day it's marked when the sheet isn't a run.
    runDate(localDay(run.date) ?? date),
    stop && stopCount ? `stop ${stop} of ${stopCount}` : "",
    run.startName ? `from ${run.startName}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const body = [];
  if (kind === "delivered") {
    const items = deliveryLines(order).map(
      (item) => `${Number(item?.item_quantity ?? 0)} × ${itemName(item)}`,
    );
    if (items.length) body.push(`Items: ${items.join(", ")}`);
  }
  if (kind === "partly") {
    const counted = lines.map((line) => ({
      name: itemName(line),
      quantity: orderedCount(line),
      delivered: deliveredCount(line),
    }));
    if (counted.length) {
      body.push(
        `Delivered: ${counted
          .map((line) => `${line.name} ${line.delivered} of ${line.quantity}`)
          .join(", ")}`,
      );
    }
    const left = counted.filter((line) => line.delivered < line.quantity);
    if (left.length) {
      body.push(
        `Still to deliver: ${left
          .map((line) => `${line.name} ${line.quantity - line.delivered}`)
          .join(", ")}`,
      );
    }
  }
  if (String(amount ?? "").trim()) body.push(`Cash received: ${amount}`);
  if (String(typed ?? "").trim()) body.push(`Note: ${String(typed).trim()}`);
  return [heading, ...body].join("\n");
};
// The note saved with photos added to a stop: what they show (the picked
// items, before delivery; the delivery, after), on which run, and what was
// typed. Saved as a comment: the order's status doesn't change.
export const photosNote = ({
  delivered,
  count,
  run = {},
  stop,
  stopCount,
  typed,
  date = new Date(),
}) => {
  const what = `${count === 1 ? "Photo" : `${count} photos`} ${
    delivered ? "of the delivery" : "of the picked items"
  }`;
  const heading = [
    what,
    run.routeName ? `${run.routeName} run` : "Delivery run",
    runDate(localDay(run.date) ?? date),
    stop && stopCount ? `stop ${stop} of ${stopCount}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const text = String(typed ?? "").trim();
  return text ? `${heading}\nNote: ${text}` : heading;
};

// `files` are File objects or { file, filename } (photos shrunk).
export const buildNoteForm = (note, files = []) => {
  const formData = new FormData();
  formData.append("note", note);
  appendFiles(formData, files);
  return formData;
};
